import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { KU } from './i18n.ku';

/**
 * Arabic / English / Kurdish (Sorani). Texts are written inline as t('عربي', 'English'); Kurdish comes
 * from the KU table keyed by the Arabic (i18n.ku.ts), or from t()'s third argument for texts with values.
 * Switching language remounts the whole app (root key) and flips its direction with the
 * root view's `direction` style, so no restart is needed on phones.
 */
export type Lang = 'ar' | 'en' | 'ku';
export const LANGS: { key: Lang; label: string }[] = [
  { key: 'ar', label: 'العربية' },
  { key: 'ku', label: 'کوردی' },
  { key: 'en', label: 'English' },
];

const KEY = 'ops_lang';
let lang: Lang = 'ar';
const listeners = new Set<() => void>();

export const getLang = () => lang;
export const isRTL = () => lang !== 'en'; // Arabic and Kurdish are written right to left
export const t = (ar: string, en: string, ku?: string) =>
  lang === 'en' ? en : lang === 'ku' ? ku ?? KU[ar] ?? ar : ar;

function applyToDocument() {
  if (Platform.OS !== 'web') return;
  document.documentElement.dir = isRTL() ? 'rtl' : 'ltr';
  document.documentElement.lang = lang === 'ku' ? 'ckb' : lang;
  document.title = t('متابعة الطلبات - دواء الحياة', 'Order Tracking - Dawaa Alhayat', 'بەدواداچوونی داواکارییەکان - دەوائ الحیاة');
}

export async function loadLang() {
  try {
    const saved = await AsyncStorage.getItem(KEY);
    if (saved === 'ar' || saved === 'en' || saved === 'ku') lang = saved;
  } catch {
    // storage unavailable: Arabic
  }
  applyToDocument();
}

export async function setLang(next: Lang) {
  lang = next;
  applyToDocument();
  try {
    await AsyncStorage.setItem(KEY, next);
  } catch {
    // not remembered; still switched for this session
  }
  listeners.forEach((fn) => fn());
}

export function useLang(): Lang {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getLang,
    getLang
  );
}

// ---- texts that come from the server (always Arabic) ----

const SERVER_EN: Record<string, string> = {
  // stages
  'طلب وارد': 'Incoming order',
  'طلب جديد': 'New order',
  'جاهز للفوترة': 'Ready to invoice',
  'بانتظار التجهيز': 'Awaiting preparation',
  'بانتظار تعيين سائق': 'Awaiting driver assignment',
  'بانتظار استلام السائق': 'Awaiting driver pickup',
  'قيد التوصيل': 'Out for delivery',
  'ذمة على السائق': 'Cash with driver',
  'بانتظار تأكيد المحصّل': 'Awaiting collector confirmation',
  'ذمة على المحصّل (بانتظار القبض)': 'Cash with collector (awaiting receipt)',
  'مغلق': 'Closed',
  'ملغي': 'Cancelled',
  'مرتجع (رُفض الاستلام)': 'Returned (refused)',
  // jobs and money parties
  'مدير': 'Admin',
  'مدير الطلبات': 'Orders admin',
  'مدير التطبيق': 'App admin',
  'لوجستك (تجهيز + سائقين + تحصيل)': 'Logistics (preparation + drivers + collection)',
  'ادمن الفوترة': 'Invoicing admin',
  'ادمن القبض': 'Receipts admin',
  'كول سنتر': 'Call center',
  'الفوترة': 'Invoicing',
  'التجهيز': 'Preparation',
  'مسؤول الشحن': 'Shipping officer',
  'سائق': 'Driver',
  'المحصّل': 'Collector',
  'القبض (الحسابات)': 'Receipts (accounts)',
  'الزبون': 'Customer',
  // history notes
  'لم يرد': 'No answer',
  'طلب التأجيل': 'Asked to postpone',
  'رد وأكّد الطلب': 'Answered and confirmed',
  'رفض الزبون الاستلام': 'Customer refused the delivery',
  'تم القبض وإغلاق الطلب': 'Payment received and order closed',
  // messages
  'مرحلة غير معروفة': 'Unknown stage',
  'هذا الحساب ليس له دور في تطبيق الطلبات': 'This account has no role in the orders app',
  'اختر نتيجة الاتصال': 'Choose the call result',
  'الإلغاء للمدير أو الكول سنتر فقط': 'Only the admin or the call center can cancel',
  'لا يمكن إلغاء الطلب بعد تسليمه للسائق': 'The order cannot be cancelled after it was handed to the driver',
  'الإرجاع لمرحلة سابقة للمدير فقط': 'Only the admin can move an order back',
  'لا يمكن إرجاع هذا الطلب لمرحلة سابقة': 'This order cannot be moved back',
  'إجراء غير معروف': 'Unknown action',
  'اختر السائق': 'Choose the driver',
  'عيّن سائقاً أولاً': 'Assign a driver first',
  'أدخل المبلغ المستلم': 'Enter the amount received',
  'الدفع للسائقين للمدير أو ادمن القبض فقط': 'Only the admin or the receipts admin can pay drivers',
  'اختر السائق والمبلغ': 'Choose the driver and the amount',
  'الطلب لم يعد في هذه المرحلة، حدّث الصفحة': 'The order is no longer at this stage, refresh the page',
  'دورك لا يسمح بهذه الخطوة': 'Your role does not allow this step',
  'هذا الطلب معيّن لسائق آخر': 'This order is assigned to another driver',
  'الطلب غير موجود في التطبيق': 'The order is not in the app',
  'هذا الحساب ليس من موظفي التطبيق': 'This account is not app staff',
  'أدخل الاسم': 'Enter the name',
  'أدخل اسم المستخدم': 'Enter the username',
  'اسم المستخدم: 3 إلى 32 حرفاً إنكليزياً صغيراً أو أرقاماً أو _ .': 'Username: 3 to 32 lowercase English letters, digits, _ or .',
  'اسم المستخدم مستخدم من قبل': 'This username is already taken',
  'أدخل كلمة المرور': 'Enter the password',
  'كلمة المرور 8 أحرف على الأقل': 'The password must be at least 8 characters',
  'اختر الدور': 'Choose the job',
  'البريد الإلكتروني غير صحيح': 'Invalid email',
  'البريد الإلكتروني مستخدم من قبل': 'This email is already taken',
  'أدخل اسم المستخدم وكلمة المرور': 'Enter the username and password',
  'اسم المستخدم أو كلمة المرور غير صحيحة': 'Wrong username or password',
  'هذا الحساب غير مفعّل في تطبيق الطلبات': 'This account is not active in the orders app',
  'تم تسجيل الخروج': 'Signed out',
  'الطلب غير موجود أو ليس من صلاحيتك': 'Order not found or not in your permissions',
  'سجّل الدخول من جديد': 'Please sign in again',
  'ليس لديك صلاحية لهذه الصفحة': 'You do not have access to this page',
  'صلاحيتك على هذه الصفحة مشاهدة فقط، لا يمكنك التعديل': 'You can only view this page, not change it',
  'ليس لديك صلاحية إلغاء الطلبات': 'You are not allowed to cancel orders',
  'ليس لديك صلاحية إرجاع الطلبات لمرحلة سابقة': 'You are not allowed to move orders back',
  'ليس لديك صلاحية إضافة حسابات بهذه الوظيفة': 'You are not allowed to add accounts for this job',
  'ليس لديك صلاحية تعديل حسابات هذه الوظيفة': 'You are not allowed to edit accounts of this job',
  'ليس لديك صلاحية تغيير كلمة مرور هذه الوظيفة': 'You are not allowed to change passwords of this job',
  'ليس لديك صلاحية تعطيل أو تفعيل حسابات هذه الوظيفة': 'You are not allowed to disable or enable accounts of this job',
  'ليس لديك صلاحية حذف حسابات هذه الوظيفة': 'You are not allowed to delete accounts of this job',
  'لا يمكنك تغيير وظيفتك أو صلاحياتك أو تعطيل حسابك بنفسك': 'You cannot change your own job or permissions, or disable yourself',
  'الصلاحيات التفصيلية يحددها مدير التطبيق فقط': 'Only the app admin sets detailed permissions',
  'لا يمكنك حذف حسابك بنفسك': 'You cannot delete your own account',
  'هذا الموظف له طلبات أو مبالغ مسجّلة، لذلك لا يُحذف حتى يبقى السجل صحيحاً. عطّل حسابه بدل الحذف.': 'This person has orders or cash on record, so the account cannot be deleted. Disable it instead.',
  'تم حذف الحساب': 'Account deleted',
  'ليس لديك صلاحية "ابدأ من الآن"': 'You are not allowed to use "Start from now"',
  'ليس لديك صلاحية تعديل الإعدادات': 'You are not allowed to change the settings',
  'اسم المستخدم: 3 إلى 64 حرفاً إنكليزياً صغيراً أو أرقاماً أو . _ - @ (يمكن أن يكون بريداً إلكترونياً)': 'Username: 3 to 64 lowercase English letters, digits or . _ - @ (an email works too)',
  'ليس لديك صلاحية': 'You are not allowed',
  'التسجيل غير متاح حالياً، تواصل مع الإدارة': 'Sign-up is closed right now, contact the management',
  'طلب تسجيلك قيد المراجعة، تقدر تدخل بعد موافقة الإدارة': 'Your sign-up is under review; you can sign in once the management approves it',
  'أدخل رقم الهاتف': 'Enter the phone number',
  'لا يوجد طلب تسجيل بهذا الرقم': 'No such sign-up request',
  'اختر وظيفة واحدة على الأقل': 'Choose at least one job',
  // New order (App\Services\StaffOrder, OpsNewOrderController)
  'اختر المحافظة': 'Choose the governorate',
  'اكتب اسم الزبون': "Enter the customer's name",
  'اكتب رقم هاتف الزبون': "Enter the customer's phone",
  'اكتب عنوان الزبون': "Enter the customer's address",
  'أضف منتجاً واحداً على الأقل': 'Add at least one product',
  'المنطقة المختارة غير موجودة في هذه المحافظة': 'The chosen area is not in this governorate',
  'اختر المنطقة، لأن المحافظة مقسّمة لمناطق بأسعار توصيل مختلفة': 'Choose the area: this governorate has areas with different delivery fees',
  'أحد المنتجات لم يعد متوفراً، احذفه من الطلب': 'A product is no longer available, remove it from the order',
  'هذه المحافظة ليست ضمن محافظاتك': 'This governorate is not one of yours',
  'تعذّر حساب الطلب، حاول مرة أخرى': 'Could not calculate the order, try again',
  // Shipping companies (FulfilmentFlow, OrderOps, OpsCarrierController)
  'مع شركة الشحن': 'With shipping company',
  'المبلغ عند شركة الشحن': 'Cash with shipping company',
  'راجع عبر شركة الشحن': 'Coming back via shipping company',
  'أجور شركات الشحن': 'Shipping company fees',
  'لا يمكن تغيير طريقة التوصيل في هذه المرحلة': 'The delivery method cannot be changed at this stage',
  'اختيار شركة الشحن للمدير أو موظف الشحن': 'Only the admin or shipping staff choose the shipping company',
  'اختر شركة الشحن': 'Choose the shipping company',
  'اختر شركة الشحن أولاً': 'Choose the shipping company first',
  'الطلب ليس عند شركة شحن': 'The order is not with a shipping company',
  'اكتب رقم الوصل أو حالة الشركة': 'Enter the waybill number or the company status',
  'أدخل أجرة الإرجاع (0 إذا ما في)': 'Enter the return fee (0 if none)',
  'وصل المرتجع من الشركة': 'Return received from the company',
  'الطلب عند شركة الشحن: سجّل "راجع عبر شركة الشحن" بدل الإلغاء': 'The order is with the shipping company: record "coming back via company" instead of cancelling',
  'تسديد شركات الشحن للمدير أو ادمن القبض فقط': 'Only the admin or receipts admin record shipping company payments',
  'اختر طلبات التسديد': 'Choose the orders being paid',
  'أدخل المبلغ المستلم من الشركة': 'Enter the amount received from the company',
  'شركة الشحن غير موجودة أو موقوفة': 'Shipping company not found or inactive',
  'شركة الشحن غير موجودة': 'Shipping company not found',
  'اكتب اسم الشركة': 'Enter the company name',
  'رابط التتبع يبدأ بـ https://، وضع {code} مكان رقم الوصل': 'The tracking link starts with https:// — put {code} where the waybill number goes',
  'أضف سعر محافظة واحدة على الأقل، أو اختر سعراً ثابتاً': 'Add a price for at least one governorate, or choose a fixed price',
  'أجرة الشركة لا تكون سالبة': "The company's fee can't be negative",
  'التوصيل بسائقينا': 'Delivered by our drivers',
};

const SERVER_PATTERNS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^محاولات كثيرة خاطئة، حاول بعد (\d+) ثانية$/, (m) => `Too many wrong attempts, try again in ${m[1]} seconds`],
  [/^المبلغ أكبر من أجور الشحن المستحقة لهذا السائق \(([\d,]+)\)$/, (m) => `The amount is more than the shipping fees owed to this driver (${m[1]})`],
  [/^تم التسليم واستلام المبلغ ([\d,]+)(?: \(فرق (-?[\d,]+)\))?$/, (m) => `Delivered, received ${m[1]}${m[2] ? ` (difference ${m[2]})` : ''}`],
  [/^إرجاع إلى: (.+)$/, (m) => `Moved back to: ${fromServer(m[1])}`],
  [/^إلغاء الطلب\s*(.*)$/, (m) => `Order cancelled${m[1] ? `: ${m[1]}` : ''}`],
  [/^شركة الشحن: (.+?)( — الشحن (.+))?$/, (m) => `Shipping company: ${m[1]}${m[3] ? ` — shipping ${m[3]}` : ''}`],
  [/^التوصيل بسائقينا — الشحن (.+)$/, (m) => `Delivered by our drivers — shipping ${m[1]}`],
  [/^شركة الشحن "(.+)" لا تغطي محافظة هذا الطلب$/, (m) => `"${m[1]}" doesn't cover this order's governorate`],
  [/^الطلب مفوتر: الشحن سيتغير من ([\d,]+) إلى ([\d,]+)/, (m) => `The order is invoiced: shipping changes from ${m[1]} to ${m[2]}. Confirm and reprint the invoice`],
  [/^سلّمت الشركة الطلب واستلمت ([\d,]+)(?: \(فرق (-?[\d,]+)\))?$/, (m) => `The company delivered it and collected ${m[1]}${m[2] ? ` (difference ${m[2]})` : ''}`],
  [/^تسديد شركة (.+?)( — أجرتها ([\d,]+))?$/, (m) => `Paid by ${m[1]}${m[3] ? ` — fee ${m[3]}` : ''}`],
  [/^الطلب #(\d+) ليس من طلبات هذه الشركة غير المسددة، حدّث الصفحة$/, (m) => `Order #${m[1]} is not an unpaid order of this company, refresh`],
  [/^تعذّر (حساب الطلب|إنشاء الطلب)\. السبب: (.+)$/s, (m) => `Could not ${m[1] === 'حساب الطلب' ? 'calculate' : 'create'} the order. Reason: ${m[2]}`],
  [/^تعذّر قراءة الموقع من الرابط/, () => 'Could not read the location from the link. Paste a Google Maps location link (maps.app.goo.gl/… or one with coordinates) or pick it on the map'],
  [/^المنتج "(.+)" ليس من مخزن هذه المحافظة$/, (m) => `"${m[1]}" is not from this governorate's store`],
  [/^الكمية غير متوفرة من "(.+)": المتوفر (\d+)$/, (m) => `Not enough "${m[1]}" in stock: ${m[2]} available`],
];

// Kurdish of the server messages that carry values (same order of ideas as SERVER_PATTERNS).
const SERVER_PATTERNS_KU: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^محاولات كثيرة خاطئة، حاول بعد (\d+) ثانية$/, (m) => `هەوڵی هەڵەی زۆر، دوای ${m[1]} چرکە دووبارە هەوڵبدەرەوە`],
  [/^المبلغ أكبر من أجور الشحن المستحقة لهذا السائق \(([\d,]+)\)$/, (m) => `بڕەکە زیاترە لە کرێی گەیاندنی شایستەی ئەم شۆفێرە (${m[1]})`],
  [/^تم التسليم واستلام المبلغ ([\d,]+)(?: \(فرق (-?[\d,]+)\))?$/, (m) => `ڕادەست کرا و ${m[1]} وەرگیرا${m[2] ? ` (جیاوازی ${m[2]})` : ''}`],
  [/^إرجاع إلى: (.+)$/, (m) => `گەڕێنرایەوە بۆ: ${fromServer(m[1])}`],
  [/^إلغاء الطلب\s*(.*)$/, (m) => `داواکارییەکە هەڵوەشێنرایەوە${m[1] ? `: ${m[1]}` : ''}`],
  [/^شركة الشحن: (.+?)( — الشحن (.+))?$/, (m) => `کۆمپانیای گەیاندن: ${m[1]}${m[3] ? ` — گەیاندن ${m[3]}` : ''}`],
  [/^التوصيل بسائقينا — الشحن (.+)$/, (m) => `گەیاندن بە شۆفێرەکانمان — گەیاندن ${m[1]}`],
  [/^شركة الشحن "(.+)" لا تغطي محافظة هذا الطلب$/, (m) => `"${m[1]}" پارێزگای ئەم داواکارییە ناگرێتەوە`],
  [/^الطلب مفوتر: الشحن سيتغير من ([\d,]+) إلى ([\d,]+)/, (m) => `داواکارییەکە پسوولە کراوە: گەیاندن لە ${m[1]}ەوە دەبێتە ${m[2]}. پشتڕاستی بکەرەوە و پسوولەکە دووبارە چاپ بکەرەوە`],
  [/^سلّمت الشركة الطلب واستلمت ([\d,]+)(?: \(فرق (-?[\d,]+)\))?$/, (m) => `کۆمپانیاکە ڕادەستی کرد و ${m[1]} وەرگرت${m[2] ? ` (جیاوازی ${m[2]})` : ''}`],
  [/^تسديد شركة (.+?)( — أجرتها ([\d,]+))?$/, (m) => `پارەدانەوەی ${m[1]}${m[3] ? ` — کرێکەی ${m[3]}` : ''}`],
  [/^الطلب #(\d+) ليس من طلبات هذه الشركة غير المسددة، حدّث الصفحة$/, (m) => `داواکاری #${m[1]} لە داواکارییە پارەنەدراوەکانی ئەم کۆمپانیایە نییە، پەڕەکە نوێ بکەرەوە`],
  [/^تعذّر (حساب الطلب|إنشاء الطلب)\. السبب: (.+)$/s, (m) => `${m[1] === 'حساب الطلب' ? 'ژماردنی' : 'دروستکردنی'} داواکارییەکە سەرکەوتوو نەبوو. هۆکار: ${m[2]}`],
  [/^تعذّر قراءة الموقع من الرابط/, () => 'خوێندنەوەی شوێن لە بەستەرەکە سەرکەوتوو نەبوو. بەستەری شوێنی Google Maps دابنێ (maps.app.goo.gl/… یان یەکێک کە کۆردیناتی تێدایە) یان لەسەر نەخشەکە هەڵیبژێرە'],
  [/^المنتج "(.+)" ليس من مخزن هذه المحافظة$/, (m) => `"${m[1]}" لە کۆگای ئەم پارێزگایە نییە`],
  [/^الكمية غير متوفرة من "(.+)": المتوفر (\d+)$/, (m) => `بڕی پێویست لە "${m[1]}" بەردەست نییە: ${m[2]} بەردەستە`],
  [/^رفض الزبون الاستلام، راجع عبر الشركة\s*(.*)$/, (m) => `کڕیار وەرگرتنی ڕەتکردەوە، لە ڕێگەی کۆمپانیاوە دەگەڕێتەوە${m[1] ? ` ${m[1]}` : ''}`],
  [/^وصل المرتجع من الشركة — أجرة الإرجاع ([\d,]+)$/, (m) => `گەڕاوەکە لە کۆمپانیاوە گەیشت — کرێی گەڕاندنەوە ${m[1]}`],
  [/^رقم الوصل: (.+?)( · حالة الشركة: (.+))?$/, (m) => `ژمارەی پسوولە: ${m[1]}${m[3] ? ` · دۆخی کۆمپانیا: ${m[3]}` : ''}`],
  [/^حالة الشركة: (.+)$/, (m) => `دۆخی کۆمپانیا: ${m[1]}`],
  [/^تحويل للاتصال إلى: (.+)$/, (m) => `نێردرا بۆ پەیوەندی: ${m[1]}`],
];

/** A label or message the server sent in Arabic, in the current language. */
export function fromServer(text: string | null | undefined): string {
  if (!text) return '';
  if (lang === 'ar') return text;
  if (lang === 'ku') {
    const key = text.trim();
    if (KU[key]) return KU[key];
    for (const [re, fn] of SERVER_PATTERNS_KU) {
      const m = key.match(re);
      if (m) return fn(m);
    }
    return text; // not translated yet: Arabic (same script)
  }
  const exact = SERVER_EN[text.trim()];
  if (exact) return exact;
  for (const [re, fn] of SERVER_PATTERNS) {
    const m = text.trim().match(re);
    if (m) return fn(m);
  }
  return text;
}
